import { useEffect, useState } from 'react'
import EmptyState from 'ui/components/EmptyState'
import documentsEmptyIcon from 'svg/documents-empty.svg'
import FloatingActionBtn from 'ui/components/FloatingActionBtn'
import DropdownField from 'ui/components/DropdownField'
import { GENERIC_ERROR_NOTIFICATION } from 'ui/messages'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import * as notifyActions from 'state/notifyActions'
import type { PartyFilters, Party, PartyKey } from './types'
import type { SelectOptions } from '../types'
import type { ApartmentSelectOptions } from '@viilu-web/shared/types/apartment'
import useDialogs from 'ui/components/dialogs/useDialogs'
import { _deleteParty } from 'state/finance-party-actions'
import DatePickerISO from 'ui/components/DatePickerISO'
import { YES_NO_MESSAGE } from 'ui/messages'
import PartyDialog from './PartyDialog'
import PartiesTable from './PartiesTable'

interface Props {
	parties: Party[]
	refreshParties: () => void
	loadingParties: boolean
	filters: PartyFilters
	setFilters: React.Dispatch<React.SetStateAction<PartyFilters>>
	selectedFinanceCompanyUUID: string
	apartmentOptions: ApartmentSelectOptions[]
	invoicingOptions: SelectOptions[]
	bondNameOptions: SelectOptions[]
	companyOptions: SelectOptions[]
	refreshBonds: () => void
}

const PartiesView = ({
	parties,
	refreshParties,
	loadingParties,
	filters,
	setFilters,
	selectedFinanceCompanyUUID,
	apartmentOptions,
	invoicingOptions,
	bondNameOptions,
	companyOptions,
	refreshBonds,
}: Props) => {
	const intl = useIntl()
	const dispatch = useDispatch()
	const { confirm } = useDialogs()
	const [addPartyDialogVisible, setAddPartyDialogVisible] =
		useState<boolean>(false)
	const [selectedParty, setSelectedParty] = useState<Party | null>(null)
	const [processing, setProcessing] = useState<boolean>(false)

	const [sort, setSort] = useState<{
		key: PartyKey
		type: string | null
		order: string
	}>({
		key: 'name',
		type: null,
		order: 'asc',
	})

	const changeSort = (key: PartyKey, type?: string) => {
		if (sort.key === key) {
			setSort((prevData) => ({
				...prevData,
				order: sort.order === 'asc' ? 'desc' : 'asc',
			}))
		} else {
			setSort({
				key: key,
				type: type || null,
				order: 'asc',
			})
		}
	}

	const yesNoOptions = [
		{ label: YES_NO_MESSAGE(intl, true), value: true },
		{ label: YES_NO_MESSAGE(intl, false), value: false },
	]

	useEffect(() => {
		if (selectedParty) {
			const party = parties.find((party) => party.uuid === selectedParty.uuid)
			if (party) {
				setSelectedParty(party)
			} else {
				setSelectedParty(null)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [parties])

	const deleteParty = async (uuid: string) => {
		const confirmMessage = intl.formatMessage({
			defaultMessage: 'Oletko varma, että haluat poistaa osapuolen?',
			description: 'Confirm message for the delete finance party.',
		})
		if (!(await confirm(confirmMessage))) {
			return
		}

		setProcessing(true)

		const result = await _deleteParty({ uuid: uuid })
		setProcessing(false)

		if (!result || result.ok === false) {
			dispatch(notifyActions.error(GENERIC_ERROR_NOTIFICATION(intl)))
			return false
		}

		dispatch(
			notifyActions.success(
				intl.formatMessage({
					defaultMessage: 'Osapuoli on poistettu.',
					description: 'Notification text of party has been deleted',
				}),
			),
		)
		refreshParties()
		return true
	}

	const renderEmpty = () => {
		return (
			<div className="flex-center flex-one full-height">
				<EmptyState
					text={intl.formatMessage({
						defaultMessage: 'Osapuolilista näyttäisi olevan tyhjä.',
						description: 'Party list is empty text.',
					})}
					icon={documentsEmptyIcon}
				/>
			</div>
		)
	}

	if (!parties) {
		renderEmpty()
	}

	const showAddPartyDialog = () => {
		setAddPartyDialogVisible(true)
	}

	const hideAddPartyDialog = () => {
		setAddPartyDialogVisible(false)
	}

	const renderFAB = () => {
		return (
			<FloatingActionBtn
				iconName={'add'}
				text={intl.formatMessage({
					defaultMessage: 'LISÄÄ OSAPUOLI',
					description: 'Add new party',
				})}
				action={showAddPartyDialog}
			/>
		)
	}

	const renderFilterSelectField = (filterProps) => {
		if (filterProps.type === 'date') {
			return (
				<DatePickerISO
					key={filterProps.label}
					style={{ marginLeft: '20px' }}
					id={filterProps.label}
					label={filterProps.label}
					value={filterProps.value}
					onChange={filterProps._onOptionSelect}
				/>
			)
		}
		if (filterProps.type === 'dropdown' && Array.isArray(filterProps.options)) {
			return (
				<div style={{ width: 180, marginRight: 5 }} key={filterProps.label}>
					<DropdownField {...filterProps} />
				</div>
			)
		}
	}

	const renderActionBar = () => {
		const filterFields = [
			{
				label: intl.formatMessage({
					defaultMessage: 'Huoneisto',
					description: 'Label of apartment',
				}),
				value: filters.apartmentUUID,
				options: apartmentOptions,
				_onOptionSelect: (value) =>
					setFilters((prevData) => ({
						...prevData,
						apartmentUUID: value,
					})),
				hasSelectAll: true,
				nullSelectAll: true,
				itemLabel: 'label',
				itemValue: 'value',
				type: 'dropdown',
			},
			{
				label: intl.formatMessage({
					defaultMessage: 'Rooli',
					description: 'Label of bond',
				}),
				value: filters.bondName,
				options: bondNameOptions,
				_onOptionSelect: (value) =>
					setFilters((prevData) => ({
						...prevData,
						bondName: value,
					})),
				hasSelectAll: true,
				nullSelectAll: true,
				itemLabel: 'label',
				itemValue: 'value',
				type: 'dropdown',
			},
			{
				label: intl.formatMessage({
					defaultMessage: 'Oletus laskutustapa',
					description: 'Label of default billing method',
				}),
				value: filters.invoicingMethod,
				options: invoicingOptions,
				_onOptionSelect: (value) =>
					setFilters((prevData) => ({
						...prevData,
						invoicingMethod: value,
					})),
				hasSelectAll: true,
				nullSelectAll: true,
				itemLabel: 'label',
				itemValue: 'value',
				type: 'dropdown',
			},
			{
				label: intl.formatMessage({
					defaultMessage: 'Aktiivinen',
					description: 'Label of active',
				}),
				value: filters.active,
				options: yesNoOptions,
				_onOptionSelect: (value) =>
					setFilters((prevData) => ({
						...prevData,
						active: value,
					})),
				hasSelectAll: true,
				nullSelectAll: true,
				itemLabel: 'label',
				itemValue: 'value',
				type: 'dropdown',
			},
			{
				label: intl.formatMessage({
					defaultMessage: 'Tarkastelupäivämäärä',
					description: 'Label of review date',
				}),
				_onOptionSelect: (value) => {
					if (!value) {
						setFilters((prevData) => ({
							...prevData,
							reviewDate: null,
						}))
					} else {
						setFilters((prevData) => ({
							...prevData,
							reviewDate: value,
						}))
					}
				},
				value: filters.reviewDate,
				nullSelectAll: true,
				itemLabel: 'label',
				itemValue: 'value',
				type: 'date',
			},
		]

		return (
			<div
				className="flex-row margin-bottom--sm margin-right"
				style={{
					justifyContent: 'space-between',
					alignItems: 'flex-end',
				}}
			>
				<div
					className="flex-row full-width"
					style={{ alignItems: 'center', marginLeft: 16, flexWrap: 'wrap' }}
				>
					{filterFields.map(renderFilterSelectField)}
				</div>
			</div>
		)
	}

	return (
		<div>
			{renderActionBar()}
			<PartiesTable
				parties={parties}
				sort={sort}
				changeSort={changeSort}
				deleteParty={deleteParty}
				apartmentOptions={apartmentOptions}
				processing={processing}
				loading={loadingParties}
			/>
			<PartyDialog
				visible={addPartyDialogVisible}
				onHide={hideAddPartyDialog}
				refreshParties={refreshParties}
				selectedFinanceCompanyUUID={selectedFinanceCompanyUUID}
				apartmentOptions={apartmentOptions}
				invoicingOptions={invoicingOptions}
				companyOptions={companyOptions}
				bondNameOptions={bondNameOptions}
				refreshBonds={refreshBonds}
			/>

			{renderFAB()}
		</div>
	)
}

export default PartiesView
